import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { push } from 'connected-react-router';

import {
  InitializeState,
  InitializeStatus
} from 'common/modules/app/initialize';
import { loadCookie, SasCookieKeys } from 'common/utils/cookie-utils';
import { GlossaryState } from 'common/modules/app/glossaries';
import { Glossary } from 'common/utils/glossary';

import { AppState } from 'pages';
import { DeleteCompletionPage } from 'pages/delete-completion/components/DeleteCompletionPage';
import { getPlanListUrl, getPurchaseUrl } from 'common/utils/urls';
import { getLogout, LOGOUT_USER } from 'common/modules/domain/user';
import { isDeletedPlan, PurchaseState } from 'common/modules/app/purchase';
import { FetchState, FetchStatus } from 'common/modules/app/fetch';

/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const initState = useSelector<AppState, InitializeState>(
    (state) => state.app.initialize
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const purchaseState = useSelector<AppState, PurchaseState>(
    (state) => state.app.purchase
  );
  const fetchState = useSelector<AppState, FetchState>(
    (state) => state.app.fetch
  );

  return {
    init: initState,
    isDeletedPlan: purchaseState.isDeletedPlan,
    isCancellationConfirmation: purchaseState.isCancellationConfirmation,
    glossary: Glossary.instance(glState),
    logoutProcess: fetchState[LOGOUT_USER]
  };
};

type SelectedState = ReturnType<typeof useStateProps>;

const useDispatchProps = (state: SelectedState) => {
  const dispatch = useDispatch();

  /** 初期化処理 */
  const init = () => {
    if (!state.isDeletedPlan) {
      // 解約時の遷移以外はプラン一覧画面へ遷移させる
      window.location.href = getPlanListUrl();
    } else {
      // 解約時は再遷移しないようにStateを変更
      dispatch(isDeletedPlan(false));
    }
    window.scrollTo(0, 0);
  };

  /** プラン一覧への遷移処理 */
  const toMovePlanList = () => {
    dispatch(push(getPlanListUrl()));
  };

  /** 契約中への遷移処理 */
  const toMoveContractList = () => {
    // 新規取得しなければ解約したプランが表示されてしまうため、window.location.hrefで遷移
    window.location.href = getPurchaseUrl();
  };

  /** ログアウトへの遷移処理 */
  const toMoveLogout = () => {
    // ログアウト実施中の場合は何もしない
    if (state.logoutProcess !== FetchStatus.PROCESSING) {
      const cookie = loadCookie();
      dispatch(getLogout(cookie[SasCookieKeys.KEY_TOKEN]));
    }
  };

  return {
    initialize: init,
    movePlanList: toMovePlanList,
    moveContractList: toMoveContractList,
    moveLogOut: toMoveLogout
  };
};

/**
 * 完了画面のContainer Component
 */
export default function ContainerComponent() {
  const _props = { ...useStateProps(), ...useDispatchProps(useStateProps()) };
  const isCompleteInit = _props.init.status === InitializeStatus.FINISH;

  React.useEffect(() => {
    _props.initialize();
  }, []);

  return isCompleteInit ? <DeleteCompletionPage {..._props} /> : null;
}
